import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, Square, AlertCircle, RefreshCw, ExternalLink } from "lucide-react";
import GlassCard from "../components/GlassCard";
import PrimaryButton, { GlassButton } from "../components/PrimaryButton";
import PageShell from "../components/PageShell";

const fmt = (s) => {
  const m = Math.floor(s / 60).toString().padStart(2, "0");
  const sec = (s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
};

const inIframe = () => {
  try { return window.self !== window.top; } catch (e) { return true; }
};

const Recording = () => {
  const nav = useNavigate();
  const clientName = sessionStorage.getItem("well_client_name") || "";
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [permState, setPermState] = useState("prompt");
  const [errorMsg, setErrorMsg] = useState("");
  const [errorKind, setErrorKind] = useState(""); // denied | iframe | nodevice | nohttps | other
  const [bars, setBars] = useState(Array(28).fill(6));
  const [iframed] = useState(() => inIframe());

  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!clientName) nav("/new-session", { replace: true });
  }, [clientName, nav]);

  useEffect(() => {
    if (navigator?.permissions?.query) {
      navigator.permissions.query({ name: "microphone" }).then((p) => {
        setPermState(p.state);
        p.onchange = () => setPermState(p.state);
      }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (audioCtxRef.current) audioCtxRef.current.close().catch(() => {});
      if (mediaRecRef.current && mediaRecRef.current.state !== "inactive") {
        mediaRecRef.current.stream.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  const openInNewTab = () => {
    window.open(window.location.href, "_blank", "noopener");
  };

  const start = async () => {
    setErrorMsg("");
    setErrorKind("");
    if (!window.isSecureContext) {
      setErrorKind("nohttps");
      setErrorMsg("Recording requires a secure (HTTPS) connection. Open the app in a normal browser tab.");
      return;
    }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setErrorKind("other");
      setErrorMsg("This browser doesn't support audio recording. Try Chrome, Safari 16+, or Firefox.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setPermState("granted");

      const mimeCandidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4;codecs=mp4a.40.2",
        "audio/mp4",
        "audio/aac",
      ];
      let mimeType = "";
      for (const m of mimeCandidates) {
        if (window.MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) {
          mimeType = m; break;
        }
      }
      const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.start();
      mediaRecRef.current = mr;
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);

      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 64;
      src.connect(analyser);
      analyserRef.current = analyser;
      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(data);
        const next = Array.from({ length: 28 }, (_, i) => {
          const v = data[i % data.length] / 255;
          return Math.max(6, Math.round(v * 56));
        });
        setBars(next);
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (e) {
      console.error("Mic error:", e?.name, e?.message);
      const name = e?.name || "";
      // Iframes commonly fail with NotAllowedError even when user granted in browser
      if (iframed && (name === "NotAllowedError" || name === "PermissionDeniedError" || name === "SecurityError")) {
        setErrorKind("iframe");
        setErrorMsg("This preview is running inside an iframe, which blocks microphone access. Open the app in its own tab to record.");
      } else if (name === "NotAllowedError" || name === "PermissionDeniedError" || name === "SecurityError") {
        setErrorKind("denied");
        setErrorMsg("Microphone permission was blocked. Re-enable it in your browser settings to continue.");
      } else if (name === "NotFoundError" || name === "DevicesNotFoundError" || name === "OverconstrainedError") {
        setErrorKind("nodevice");
        setErrorMsg("No microphone was found. Plug one in or pick a different audio device.");
      } else {
        setErrorKind("other");
        setErrorMsg((e?.name ? e.name + ": " : "") + (e?.message || "Could not start recording."));
      }
    }
  };

  const stop = async () => {
    if (!mediaRecRef.current) return;
    const mr = mediaRecRef.current;
    const stream = mr.stream;
    await new Promise((resolve) => {
      mr.onstop = resolve;
      mr.stop();
    });
    stream.getTracks().forEach((t) => t.stop());
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    if (audioCtxRef.current) audioCtxRef.current.close().catch(() => {});

    const recordedType = mr.mimeType || (chunksRef.current[0] && chunksRef.current[0].type) || "audio/webm";
    const blob = new Blob(chunksRef.current, { type: recordedType });
    const arrBuf = await blob.arrayBuffer();
    window._wellPendingAudio = { buffer: arrBuf, type: recordedType, clientName };
    nav("/processing", { state: { hasAudio: true } });
  };

  const showError = errorKind !== "";

  return (
    <PageShell showSettings>
      <div className="pt-16 pb-32 max-w-md mx-auto flex flex-col items-center gap-8 well-page-enter">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-[#2A3B32]">AI Listen In</h1>
          <p className="mt-1 text-sm text-[#8A9C91]">Session: {clientName || "your session"}</p>
          <p className="mt-1 text-xs text-[#8A9C91]">No audio is saved — only your transcript and notes.</p>
        </div>

        {iframed && !showError && !recording && (
          <GlassCard className="w-full" data-testid="iframe-warning">
            <div className="flex items-start gap-3">
              <AlertCircle size={20} className="text-[#8B6E1F] flex-shrink-0 mt-0.5" />
              <div className="text-sm text-[#4A5D52] leading-relaxed">
                <p className="font-semibold text-[#2A3B32]">Opening in a preview iframe</p>
                <p className="mt-1">Most browsers block the mic in iframes. If recording fails, tap below to open the app in its own tab.</p>
                <button
                  onClick={openInNewTab}
                  data-testid="open-new-tab-btn"
                  className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-[#8BA38E] text-white rounded-full text-xs font-semibold hover:bg-[#7A917D] transition-colors"
                >
                  <ExternalLink size={14} /> Open in new tab
                </button>
              </div>
            </div>
          </GlassCard>
        )}

        {showError ? (
          <GlassCard className="w-full" data-testid="mic-error-card">
            <div className="flex flex-col items-center text-center gap-4">
              <div className="w-14 h-14 rounded-full bg-[#E0B96A]/20 border border-[#E0B96A]/40 flex items-center justify-center">
                <AlertCircle size={26} className="text-[#8B6E1F]" />
              </div>
              <h3 className="text-lg font-semibold text-[#2A3B32]">
                {errorKind === "iframe" ? "Iframe blocks the microphone" :
                 errorKind === "denied" ? "Microphone access needed" :
                 errorKind === "nodevice" ? "No microphone found" :
                 errorKind === "nohttps" ? "Secure connection needed" :
                 "Recording error"}
              </h3>
              <p className="text-sm text-[#4A5D52] leading-relaxed">{errorMsg}</p>

              {errorKind === "iframe" && (
                <>
                  <PrimaryButton onClick={openInNewTab} data-testid="open-new-tab-error-btn">
                    <ExternalLink size={18} className="mr-2" /> Open in New Tab
                  </PrimaryButton>
                  <p className="text-xs text-[#8A9C91]">
                    Once the app opens in a new tab, the browser will ask for microphone permission directly.
                  </p>
                </>
              )}

              {errorKind === "denied" && (
                <>
                  <div className="text-left text-xs text-[#4A5D52] bg-white/40 rounded-[12px] p-3 w-full leading-relaxed">
                    <p className="font-semibold mb-1">To re-enable:</p>
                    <p>• <b>Chrome / Edge</b>: tap the lock icon in the address bar → Site settings → Microphone → Allow → refresh page</p>
                    <p>• <b>Safari (Mac)</b>: Safari menu → Settings for This Website → Microphone → Allow → refresh</p>
                    <p>• <b>iPhone Safari</b>: Settings app → Safari → Microphone → Allow</p>
                  </div>
                  <PrimaryButton onClick={() => { setErrorKind(""); setErrorMsg(""); start(); }} data-testid="mic-retry-btn">
                    <RefreshCw size={18} className="mr-2" /> Try Again
                  </PrimaryButton>
                </>
              )}

              {(errorKind === "nodevice" || errorKind === "nohttps" || errorKind === "other") && (
                <PrimaryButton onClick={() => { setErrorKind(""); setErrorMsg(""); start(); }} data-testid="mic-retry-btn">
                  <RefreshCw size={18} className="mr-2" /> Try Again
                </PrimaryButton>
              )}

              <GlassButton onClick={() => nav("/new-session", { replace: true })} data-testid="switch-to-write-notes">
                Switch to Write Notes
              </GlassButton>
            </div>
          </GlassCard>
        ) : (
          <>
            <div className="relative flex items-center justify-center mt-4">
              <button
                onClick={recording ? null : start}
                disabled={recording}
                className={`relative w-32 h-32 rounded-full bg-[#8BA38E] flex items-center justify-center shadow-[0_8px_32px_rgba(139,163,142,0.4)] transition-transform hover:scale-105 ${recording ? "well-pulse" : ""}`}
                data-testid="record-btn"
              >
                <Mic size={48} strokeWidth={1.8} className="text-white" />
              </button>
            </div>

            <div className="text-4xl font-semibold tabular-nums text-[#2A3B32]" data-testid="recording-timer">
              {fmt(elapsed)}
            </div>

            <div className="flex items-end gap-1 h-16" data-testid="waveform">
              {bars.map((b, i) => (
                <span key={i} className="w-1.5 rounded-full bg-[#8BA38E]/80 transition-all" style={{ height: `${b}px` }} />
              ))}
            </div>

            {recording ? (
              <PrimaryButton onClick={stop} data-testid="stop-process-btn">
                <Square size={18} className="mr-2" /> Stop and Process
              </PrimaryButton>
            ) : (
              <>
                <p className="text-xs text-[#8A9C91] text-center px-4">
                  Tap the microphone to start. You&apos;ll be asked for permission the first time.
                </p>
                <GlassButton onClick={() => nav(-1)} data-testid="recording-cancel-btn">Back</GlassButton>
              </>
            )}
          </>
        )}
      </div>
    </PageShell>
  );
};

export default Recording;
